<div class="tco-card" *ngIf="data && hasTcoLayout(); else defaultComparisonChart">
  <header class="tco-header">
    <div class="tco-title-row">
      <h2>{{ chartTitle }}</h2>
      <span class="unit-badge" *ngIf="unitBadge">{{ unitBadge }}</span>
    </div>
    <div class="tco-subtitle" *ngIf="chartSubtitle">{{ chartSubtitle }}</div>
  </header>

  <section class="tco-visual">
    <div class="legend-row" *ngIf="legendItems.length">
      <div class="legend-item" *ngFor="let item of legendItems">
        <span class="legend-swatch" [style.background]="item.color"></span>
        <span>{{ item.label }}</span>
      </div>
    </div>

    <ng-template #miniBarsWithConnectors let-group="group">
      <div
        class="mini-chart"
        [style.width.px]="getMiniTrackWidth(group)"
        [style.height.px]="getMiniChartHeight(group)">
        <div class="mini-comparison-overlay" *ngIf="shouldShowTcoComparisons(group)">
          <ng-container *ngFor="let comparison of getTcoComparisons(group); let i = index">
            <div
              class="mini-comparison-branch"
              *ngIf="i > 0"
              [style.left.px]="getMiniBarCenterX(0)"
              [style.width.px]="getMiniConnectorWidth(i)"
              [style.top.px]="getMiniConnectorTop(group, i)">
              <span class="mini-comparison-line"></span>
              <span
                class="mini-comparison-stem left"
                [style.height.px]="getMiniConnectorStemHeight(group, 0, i)">
              </span>
              <span
                class="mini-comparison-stem right"
                [style.height.px]="getMiniConnectorStemHeight(group, i, i)">
              </span>
              <span
                class="mini-comparison-bubble"
                [class.positive]="getPercentClass(comparison) === 'positive'"
                [class.negative]="getPercentClass(comparison) === 'negative'">
                {{ formatPercent(comparison) }}
              </span>
            </div>
          </ng-container>
        </div>

        <div class="mini-bars" [style.height.px]="getMiniBarAreaHeight(group)">
          <span
            class="mini-bar"
            *ngFor="let value of group.values; let i = index"
            [style.height.px]="getMiniBarHeight(group, value)"
            [style.background]="getMiniBarColor(i)">
          </span>
        </div>
      </div>
    </ng-template>

    <div class="formula-row">
      <div class="left-stack">
        <div class="top-components">
          <ng-container *ngFor="let group of getNumeratorGroups(); let groupIndex = index">
            <div class="mini-group">
              <ng-container *ngTemplateOutlet="miniBarsWithConnectors; context: { group: group }"></ng-container>
              <div class="mini-label">{{ group.label }}</div>
            </div>

            <div class="formula-symbol" *ngIf="groupIndex < getNumeratorGroups().length - 1">
              {{ getFormulaOperator('numerator', groupIndex) }}
            </div>
          </ng-container>
        </div>

        <div class="stack-divider"></div>

        <ng-container *ngIf="getDenominatorGroup() as denominator">
          <div class="mini-group lower-group">
            <ng-container *ngTemplateOutlet="miniBarsWithConnectors; context: { group: denominator }"></ng-container>
            <div class="mini-label">{{ denominator.label }}</div>
          </div>
        </ng-container>
      </div>

      <div class="formula-symbol large" *ngIf="getAdditiveGroups().length">
        {{ getFormulaOperator('additive') }}
      </div>

      <ng-container *ngFor="let group of getAdditiveGroups()">
        <div class="mini-group fuel-group">
          <ng-container *ngTemplateOutlet="miniBarsWithConnectors; context: { group: group }"></ng-container>
          <div class="mini-label">{{ group.label }}</div>
          <div class="mini-subtitle" *ngIf="group.subtitle">{{ group.subtitle }}</div>
        </div>
      </ng-container>

      <div class="formula-symbol large">{{ getFormulaOperator('equals') }}</div>

      <ng-container *ngIf="getResultGroup() as total">
        <div class="mini-group total-group">
          <ng-container *ngTemplateOutlet="miniBarsWithConnectors; context: { group: total }"></ng-container>
          <div class="mini-label">{{ total.label }}</div>
          <div class="mini-subtitle" *ngIf="total.subtitle">{{ total.subtitle }}</div>
        </div>
      </ng-container>
    </div>
  </section>

  <section class="summary-section" *ngIf="summaryTitle || summaryText">
    <h3 *ngIf="summaryTitle">{{ summaryTitle }}</h3>
    <p *ngIf="summaryText">{{ summaryText }}</p>
  </section>

  <footer
    class="metric-footer"
    *ngIf="footerMetrics.length"
    [style.--metric-count]="footerMetrics.length"
  >
    <div class="footer-metric" *ngFor="let metric of footerMetrics">
      <span class="metric-label">{{ metric.label }}</span>
      <span class="metric-value" [ngClass]="getFooterMetricClass(metric)">
        <span class="confidence-dot" *ngIf="isConfidenceMetric(metric)"></span>
        {{ formatMetricValue(metric) }}
      </span>
    </div>
  </footer>
</div>

<ng-template #defaultComparisonChart>
<div class="chart-wrapper" *ngIf="data">
  <div class="bars-container" [style.padding-top.px]="getTopPadding()">
    <div class="bars-track">
      <div class="connector-line"
           *ngIf="shouldShowConnector() && !isFirstBarMode()"
           [style.bottom.px]="getConnectorBottom()"></div>

      <div class="first-bar-overlay" *ngIf="shouldShowConnector() && isFirstBarMode()">
        <div class="first-bar-trunk"
             [style.bottom.px]="getTipBottom(0)"
             [style.height.px]="getFirstBarTrunkHeight()"></div>

        <div class="first-bar-branch"
             *ngFor="let value of values; let i = index"
             [style.display]="i > 0 && hasComparison(i) ? 'block' : 'none'"
             [style.bottom.px]="getFirstBarBranchBottom(i)"
             [style.width]="getFirstBarBranchWidth(i)">
          <div class="first-bar-line"></div>
          <div class="first-bar-right-stem"
               [style.height.px]="getFirstBarRightStemHeight(i)"></div>
          <div class="pair-bubble first-bar-bubble"
               *ngIf="shouldShowComparisonBubbles()"
               [style.bottom.px]="getFirstBarBubbleBottom(i)">
            {{ formatPercent(getBarComparison(i)!) }}
          </div>
        </div>
      </div>

      <div class="bar-block"
           *ngFor="let value of values; let i = index">

        <div class="bar-tip"
             *ngIf="shouldShowConnector() && !isFirstBarMode()"
             [style.bottom.px]="getTipBottom(i)"
             [style.height.px]="getTipHeight(i)"></div>

        <div class="pair-bubble"
             *ngIf="shouldShowConnector() && shouldShowComparisonBubbles() && !isFirstBarMode() && hasComparison(i)"
             [style.bottom.px]="getBubbleOnConnectorBottom()">
          {{ formatPercent(getBarComparison(i)!) }}
        </div>

        <div class="pair-bubble top-bubble"
             *ngIf="!shouldShowConnector() && shouldShowComparisonBubbles() && hasComparison(i)">
          {{ formatPercent(getBarComparison(i)!) }}
        </div>

        <div class="value" *ngIf="shouldShowValues()">{{ value }}</div>
        <div class="bar"
             [style.background]="getBarColor(i)"
             [style.height.px]="getBarHeight(value)">
        </div>
        <div class="label" *ngIf="shouldShowLabels()">{{ labels[i] }}</div>
      </div>
    </div>
  </div>
  <div class="axis-label" *ngIf="getBottomLabel()">{{ getBottomLabel() }}</div>
</div>
</ng-template>
